import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes, styled } from '@mui/material/styles';

// Keyframe Animations
const pulseGlow = keyframes`
  0%, 100% {
    filter: drop-shadow(0 0 12px rgba(255, 183, 3, 0.6)) drop-shadow(0 0 24px rgba(255, 120, 0, 0.3));
    transform: scale(1);
  }
  50% {
    filter: drop-shadow(0 0 22px rgba(255, 215, 0, 0.9)) drop-shadow(0 0 35px rgba(255, 140, 0, 0.5));
    transform: scale(1.02);
  }
`;

const flameFlicker = keyframes`
  0%, 100% {
    transform: scale(1) rotate(-1deg);
    opacity: 0.95;
  }
  25% {
    transform: scale(1.1, 1.15) rotate(2deg);
    opacity: 1;
  }
  50% {
    transform: scale(0.95, 0.9) rotate(-2deg);
    opacity: 0.85;
  }
  75% {
    transform: scale(1.05, 1.08) rotate(1deg);
    opacity: 0.98;
  }
`;

const rotateRays = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const shimmerText = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

const floatSparkle = keyframes`
  0% {
    transform: translateY(0px) scale(0.6);
    opacity: 0;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    transform: translateY(-40px) scale(1.2);
    opacity: 0;
  }
`;

const FestiveContainer = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  user-select: none;
  overflow: hidden;
`;

const GaneshaWrapper = styled(Box)`
  animation: ${pulseGlow} 3s ease-in-out infinite;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
`;

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  duration: number;
  delay: number;
}

interface Props {
  isMenuOpen?: boolean;
  children: React.ReactNode; // Original logo element
}

type AnimationPhase = 'LOGO_SHOW' | 'LOGO_EXIT' | 'GANESHA_ENTER' | 'GANESHA_HOLD' | 'GANESHA_EXIT';

export const GaneshFestiveAnimation: React.FC<Props> = ({ isMenuOpen = true, children }) => {
  // Infinite Loop Phase State Machine
  const [phase, setPhase] = useState<AnimationPhase>('LOGO_SHOW');
  const timersRef = useRef<NodeJS.Timeout[]>([]);

  useEffect(() => {
    // Check if user prefers reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setPhase('LOGO_SHOW');
      return;
    }

    let isMounted = true;

    const clearAllTimers = () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };

    // Infinite Loop Cycle
    // 0ms - 2500ms: Original Logo visible
    // 2500ms - 3300ms: Logo Exit transition
    // 3300ms - 4500ms: Ganesha Reveal
    // 4500ms - 7000ms: Ganesha + Greetings Hold State
    // 7000ms - 7800ms: Ganesha Exit transition
    // 7800ms: Restart Cycle
    const runCycle = () => {
      clearAllTimers();
      if (!isMounted) return;

      setPhase('LOGO_SHOW');

      timersRef.current.push(
        setTimeout(() => {
          if (isMounted) setPhase('LOGO_EXIT');
        }, 2500),
      );

      timersRef.current.push(
        setTimeout(() => {
          if (isMounted) setPhase('GANESHA_ENTER');
        }, 3300),
      );

      timersRef.current.push(
        setTimeout(() => {
          if (isMounted) setPhase('GANESHA_HOLD');
        }, 4500),
      );

      timersRef.current.push(
        setTimeout(() => {
          if (isMounted) setPhase('GANESHA_EXIT');
        }, 7000),
      );

      timersRef.current.push(
        setTimeout(() => {
          if (isMounted) runCycle();
        }, 7800),
      );
    };

    runCycle();

    return () => {
      isMounted = false;
      clearAllTimers();
    };
  }, []);

  // Generate floating gold sparkle particles
  const particles: Particle[] = useRef(
    Array.from({ length: 12 }, (_, i) => ({
      id: i,
      x: ((i * 8 + (i % 3) * 15) % 90) + 5,
      y: 60 + (i % 4) * 10,
      size: 3 + (i % 4) * 2,
      duration: 2.5 + (i % 3) * 0.8,
      delay: (i % 5) * 0.4,
    })),
  ).current;

  // Calculate container heights & scales based on menu open status
  const containerMinHeight = isMenuOpen ? '160px' : '90px';
  const scaleFactor = isMenuOpen ? 1 : 0.65;

  return (
    <FestiveContainer style={{ minHeight: containerMinHeight }}>
      {/* 1. ORIGINAL LOGO WITH SMOOTH TRANSITIONS */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: phase === 'LOGO_SHOW' ? 1 : 0,
          transform:
            phase === 'LOGO_SHOW'
              ? 'scale(1) translateY(0)'
              : phase === 'LOGO_EXIT'
              ? 'scale(1.12) translateY(-6px)'
              : 'scale(0.85) translateY(10px)',
          filter: phase === 'LOGO_EXIT' ? 'blur(6px) drop-shadow(0 0 15px #FFD700)' : 'none',
          pointerEvents: phase === 'LOGO_SHOW' ? 'auto' : 'none',
        }}
      >
        {children}
      </Box>

      {/* 2. LORD GANESHA + LADDOO + FESTIVE COMPOSITION */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: phase === 'GANESHA_ENTER' || phase === 'GANESHA_HOLD' ? 1 : 0,
          transform:
            phase === 'GANESHA_ENTER' || phase === 'GANESHA_HOLD'
              ? `scale(${scaleFactor}) translateY(0)`
              : phase === 'GANESHA_EXIT'
              ? `scale(${scaleFactor * 0.9}) translateY(-10px)`
              : `scale(${scaleFactor * 0.7}) translateY(15px)`,
          filter: phase === 'GANESHA_EXIT' ? 'blur(4px)' : 'none',
          pointerEvents: phase === 'GANESHA_ENTER' || phase === 'GANESHA_HOLD' ? 'auto' : 'none',
        }}
      >
        {/* Divine Rotating Sunburst Light Rays */}
        <Box
          sx={{
            position: 'absolute',
            width: isMenuOpen ? '200px' : '130px',
            height: isMenuOpen ? '200px' : '130px',
            top: '50%',
            left: '50%',
            marginTop: isMenuOpen ? '-100px' : '-65px',
            marginLeft: isMenuOpen ? '-100px' : '-65px',
            zIndex: 0,
            opacity: 0.35,
            animation: `${rotateRays} 25s linear infinite`,
            pointerEvents: 'none',
          }}
        >
          <svg viewBox="0 0 200 200" width="100%" height="100%">
            <defs>
              <radialGradient id="rayGrad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#FFD700" stopOpacity="0.8" />
                <stop offset="60%" stopColor="#FF9800" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#FF5722" stopOpacity="0" />
              </radialGradient>
            </defs>
            <g fill="url(#rayGrad)">
              {[...Array(12)].map((_, i) => (
                <polygon key={i} points="100,100 93,0 107,0" transform={`rotate(${i * 30} 100 100)`} />
              ))}
            </g>
          </svg>
        </Box>

        {/* Floating Golden Dust Particles */}
        {particles.map(p => (
          <Box
            key={p.id}
            sx={{
              position: 'absolute',
              left: `${p.x}%`,
              bottom: `${p.y}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              borderRadius: '50%',
              backgroundColor: '#FFD700',
              boxShadow: '0 0 6px #FFA000, 0 0 10px #FFF',
              animation: `${floatSparkle} ${p.duration}s ease-in-out infinite`,
              animationDelay: `${p.delay}s`,
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        ))}

        {/* Main Ganesha Artwork & Laddoos Container */}
        <GaneshaWrapper sx={{ zIndex: 2 }}>
          <svg
            width={isMenuOpen ? '140' : '90'}
            height={isMenuOpen ? '110' : '72'}
            viewBox="0 0 220 170"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              {/* Gradients */}
              <linearGradient id="goldGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FFF176" />
                <stop offset="40%" stopColor="#FFD54F" />
                <stop offset="80%" stopColor="#FFB300" />
                <stop offset="100%" stopColor="#FF8F00" />
              </linearGradient>

              <linearGradient id="saffronGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#FF5722" />
                <stop offset="50%" stopColor="#F4511E" />
                <stop offset="100%" stopColor="#E64A19" />
              </linearGradient>

              <radialGradient id="auraGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#FFE082" stopOpacity="0.9" />
                <stop offset="50%" stopColor="#FFB300" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#FF6F00" stopOpacity="0" />
              </radialGradient>

              <linearGradient id="thaliGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FFE082" />
                <stop offset="50%" stopColor="#FFB300" />
                <stop offset="100%" stopColor="#E65100" />
              </linearGradient>

              <radialGradient id="laddooGrad" cx="35%" cy="35%" r="65%">
                <stop offset="0%" stopColor="#FFF59D" />
                <stop offset="40%" stopColor="#FFB74D" />
                <stop offset="85%" stopColor="#F57C00" />
                <stop offset="100%" stopColor="#E65100" />
              </radialGradient>

              <filter id="glowFilter" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
            </defs>

            {/* Glowing Backdrop Aura */}
            <circle cx="110" cy="75" r="65" fill="url(#auraGlow)" />

            {/* FLANKING DIYA LAMPS */}
            {/* Left Diya */}
            <g transform="translate(18, 110)">
              <path d="M 0 15 Q 12 28 24 15 Q 12 5 0 15 Z" fill="url(#thaliGrad)" />
              <ellipse cx="12" cy="14" rx="8" ry="3" fill="#E65100" />
              {/* Flame */}
              <path
                d="M 12 12 Q 7 2 12 -4 Q 17 2 12 12 Z"
                fill="url(#goldGrad1)"
                filter="url(#glowFilter)"
                style={{ animation: `${flameFlicker} 1.5s ease-in-out infinite` }}
              />
              <path
                d="M 12 10 Q 9 4 12 0 Q 15 4 12 10 Z"
                fill="#FF3D00"
                style={{ animation: `${flameFlicker} 1.2s ease-in-out infinite` }}
              />
            </g>

            {/* Right Diya */}
            <g transform="translate(178, 110)">
              <path d="M 0 15 Q 12 28 24 15 Q 12 5 0 15 Z" fill="url(#thaliGrad)" />
              <ellipse cx="12" cy="14" rx="8" ry="3" fill="#E65100" />
              {/* Flame */}
              <path
                d="M 12 12 Q 7 2 12 -4 Q 17 2 12 12 Z"
                fill="url(#goldGrad1)"
                filter="url(#glowFilter)"
                style={{ animation: `${flameFlicker} 1.7s ease-in-out infinite` }}
              />
              <path
                d="M 12 10 Q 9 4 12 0 Q 15 4 12 10 Z"
                fill="#FF3D00"
                style={{ animation: `${flameFlicker} 1.4s ease-in-out infinite` }}
              />
            </g>

            {/* LORD GANESHA SILHOUETTE & FEATURES */}

            {/* Supa Ears (Left & Right) */}
            {/* Left Ear */}
            <path
              d="M 80 55 C 50 40 40 75 75 90 C 85 95 90 80 80 55 Z"
              fill="url(#goldGrad1)"
              stroke="#FF8F00"
              strokeWidth="2"
              opacity="0.9"
            />
            <path d="M 72 58 C 55 48 48 70 72 82" stroke="#FF6F00" strokeWidth="1.5" fill="none" />

            {/* Right Ear */}
            <path
              d="M 140 55 C 170 40 180 75 145 90 C 135 95 130 80 140 55 Z"
              fill="url(#goldGrad1)"
              stroke="#FF8F00"
              strokeWidth="2"
              opacity="0.9"
            />
            <path d="M 148 58 C 165 48 172 70 148 82" stroke="#FF6F00" strokeWidth="1.5" fill="none" />

            {/* Head & Crown (Mukut) */}
            {/* Crown Base */}
            <path
              d="M 82 48 Q 110 38 138 48 L 134 32 Q 110 22 86 32 Z"
              fill="url(#goldGrad1)"
              stroke="#E65100"
              strokeWidth="1.5"
            />
            {/* Crown Middle Tier */}
            <path
              d="M 88 32 Q 110 20 132 32 L 126 14 Q 110 6 94 14 Z"
              fill="url(#goldGrad1)"
              stroke="#E65100"
              strokeWidth="1.5"
            />
            {/* Crown Top Finial (Kalash / Jewel) */}
            <path d="M 104 14 Q 110 2 116 14 Z" fill="#D50000" />
            <circle cx="110" cy="6" r="3.5" fill="url(#goldGrad1)" filter="url(#glowFilter)" />
            {/* Crown Jewels Accent */}
            <circle cx="110" cy="24" r="3" fill="#D50000" />
            <circle cx="98" cy="38" r="2.5" fill="#29B6F6" />
            <circle cx="122" cy="38" r="2.5" fill="#29B6F6" />

            {/* Main Head Structure */}
            <path
              d="M 82 48 Q 110 54 138 48 Q 142 80 110 92 Q 78 80 82 48 Z"
              fill="url(#goldGrad1)"
              stroke="#FF6F00"
              strokeWidth="1"
            />

            {/* Sacred Tilak & Trishul on Forehead */}
            <path d="M 104 46 Q 110 38 116 46 Q 110 68 104 46 Z" fill="url(#saffronGrad)" />
            <path d="M 100 50 Q 110 56 120 50" stroke="#D50000" strokeWidth="2.5" fill="none" strokeLinecap="round" />
            <circle cx="110" cy="55" r="2.5" fill="#FFF" />

            {/* Expressive Eyes */}
            <path d="M 90 62 Q 97 58 102 63" stroke="#5D4037" strokeWidth="2" fill="none" strokeLinecap="round" />
            <path d="M 118 63 Q 123 58 130 62" stroke="#5D4037" strokeWidth="2" fill="none" strokeLinecap="round" />

            {/* Tusk (Ekadanta) - Left side tusk */}
            <path d="M 96 78 L 91 84 L 97 82 Z" fill="#FFF" stroke="#B0BEC5" strokeWidth="0.5" />

            {/* Trunk (Vakratunda) gracefully curling to left holding a laddoo */}
            <path
              d="M 110 70 C 114 85 125 92 120 106 C 114 120 90 114 88 102 C 86 94 96 90 100 95 C 102 98 96 103 93 100"
              fill="none"
              stroke="url(#goldGrad1)"
              strokeWidth="11"
              strokeLinecap="round"
            />
            <path
              d="M 110 70 C 114 85 125 92 120 106 C 114 120 90 114 88 102 C 86 94 96 90 100 95 C 102 98 96 103 93 100"
              fill="none"
              stroke="#E65100"
              strokeWidth="1.5"
              strokeLinecap="round"
            />

            {/* Laddoo held in Trunk tip */}
            <circle cx="95" cy="95" r="5" fill="url(#laddooGrad)" filter="url(#glowFilter)" />
            <circle cx="94" cy="94" r="1.2" fill="#FFF" />

            {/* Abhaya Mudra (Right Hand Blessing) */}
            <g transform="translate(142, 75)">
              <path
                d="M 0 15 C 8 5 18 8 16 22 C 14 28 4 28 0 15 Z"
                fill="url(#goldGrad1)"
                stroke="#E65100"
                strokeWidth="1"
              />
              {/* Om symbol on palm */}
              <text x="5" y="20" fontSize="7" fontWeight="bold" fill="#D50000" fontFamily="serif">
                ॐ
              </text>
            </g>

            {/* PROMINENT GOLDEN THALI & LADDOOS IN FRONT */}
            <g transform="translate(55, 125)">
              {/* Outer Golden Plate / Thali Base */}
              <ellipse
                cx="55"
                cy="22"
                rx="55"
                ry="14"
                fill="url(#thaliGrad)"
                stroke="#FFE082"
                strokeWidth="2"
                filter="url(#glowFilter)"
              />
              {/* Inner Thali Rim */}
              <ellipse cx="55" cy="21" rx="48" ry="11" fill="#BF360C" stroke="#FFD54F" strokeWidth="1" />
              <ellipse cx="55" cy="21" rx="44" ry="9" fill="url(#thaliGrad)" opacity="0.4" />

              {/* PYRAMID STACK OF DELICIOUS MOTICHOOR LADDOOS */}
              {/* Bottom Layer Laddoos (5 Laddoos) */}
              <circle cx="25" cy="18" r="7.5" fill="url(#laddooGrad)" />
              <circle cx="23" cy="16" r="1.5" fill="#FFF" opacity="0.8" />
              <circle cx="27" cy="19" r="1" fill="#2E7D32" />

              <circle cx="40" cy="19" r="8" fill="url(#laddooGrad)" />
              <circle cx="38" cy="17" r="1.5" fill="#FFF" opacity="0.8" />
              <circle cx="42" cy="20" r="1" fill="#D50000" />

              <circle cx="55" cy="20" r="8.5" fill="url(#laddooGrad)" />
              <circle cx="53" cy="18" r="1.8" fill="#FFF" opacity="0.9" />
              <circle cx="57" cy="21" r="1.2" fill="#2E7D32" />

              <circle cx="70" cy="19" r="8" fill="url(#laddooGrad)" />
              <circle cx="68" cy="17" r="1.5" fill="#FFF" opacity="0.8" />
              <circle cx="72" cy="20" r="1" fill="#D50000" />

              <circle cx="85" cy="18" r="7.5" fill="url(#laddooGrad)" />
              <circle cx="83" cy="16" r="1.5" fill="#FFF" opacity="0.8" />
              <circle cx="87" cy="19" r="1" fill="#2E7D32" />

              {/* Middle Layer Laddoos (3 Laddoos) */}
              <circle cx="33" cy="12" r="7.5" fill="url(#laddooGrad)" />
              <circle cx="31" cy="10" r="1.5" fill="#FFF" opacity="0.8" />

              <circle cx="55" cy="12" r="8" fill="url(#laddooGrad)" />
              <circle cx="53" cy="10" r="1.6" fill="#FFF" opacity="0.9" />
              <circle cx="56" cy="13" r="1" fill="#2E7D32" />

              <circle cx="77" cy="12" r="7.5" fill="url(#laddooGrad)" />
              <circle cx="75" cy="10" r="1.5" fill="#FFF" opacity="0.8" />

              {/* Top Peak Laddoo (Crown Laddoo with Silver Vark highlight) */}
              <circle cx="55" cy="4" r="8.5" fill="url(#laddooGrad)" filter="url(#glowFilter)" />
              <circle cx="53" cy="2" r="2" fill="#FFF" />
              <circle cx="56" cy="5" r="1.2" fill="#2E7D32" />
              <circle cx="54" cy="6" r="1" fill="#D50000" />
            </g>
          </svg>
        </GaneshaWrapper>

        {/* 3. TASTEFUL FESTIVE GREETINGS */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mt: isMenuOpen ? 1 : 0.5,
            textAlign: 'center',
            px: 1,
          }}
        >
          {/* Main Greeting */}
          <Typography
            variant={isMenuOpen ? 'h6' : 'subtitle2'}
            sx={{
              fontWeight: 800,
              fontSize: isMenuOpen ? '0.95rem' : '0.75rem',
              letterSpacing: '0.04em',
              background: 'linear-gradient(90deg, #FF6F00 0%, #FFD700 50%, #FF8F00 100%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: `${shimmerText} 4s linear infinite`,
              textShadow: '0 2px 10px rgba(255, 160, 0, 0.3)',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
            }}
          >
            Happy Ganesh Chaturthi
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: isMenuOpen ? '0.72rem' : '0.6rem',
              color: '#E65100',
              letterSpacing: '0.06em',
              mt: '2px',
              opacity: 0.9,
              whiteSpace: 'nowrap',
              textTransform: 'uppercase',
            }}
          >
            Ganpati Bappa Morya
          </Typography>
        </Box>
      </Box>
    </FestiveContainer>
  );
};

export default GaneshFestiveAnimation;
